import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Restaurant } from '../types';
import { negociosService, Negocio } from '../src/services/negociosService';
import { smsVerificationService } from '../src/services/smsVerificationService';
import { useTheme } from '../src/contexts/ThemeContext';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

gsap.registerPlugin(ScrollTrigger);

// Fix Leaflet default icon paths broken by bundlers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const MapClickHandler: React.FC<{ onDblClick: (lat: number, lng: number) => void }> = ({ onDblClick }) => {
  useMapEvents({
    dblclick(e) {
      onDblClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

const MapFlyTo: React.FC<{ center: [number, number] }> = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    const delays = [0, 100, 300, 600, 1000];
    const timers = delays.map(d => setTimeout(() => map.invalidateSize(), d));
    const container = map.getContainer();
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => map.invalidateSize());
      ro.observe(container);
    }
    return () => {
      timers.forEach(clearTimeout);
      ro?.disconnect();
    };
  }, [map]);
  useEffect(() => { map.flyTo(center, 16, { duration: 1.2 }); }, [center, map]);
  return null;
};

interface HomeProps {
  onRestaurantSelect: (restaurant: Restaurant) => void;
  searchQuery?: string;
}

type SortKey = 'recommended' | 'rating' | 'deliveryTime' | 'deliveryFee';

const Home: React.FC<HomeProps> = ({ onRestaurantSelect, searchQuery = '' }) => {
  const { theme } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const categoriesRef = useRef<HTMLDivElement>(null);
  const offersRef = useRef<HTMLDivElement>(null);
  const restaurantsRef = useRef<HTMLDivElement>(null);

  const [categories, setCategories] = useState<{ name: string; icon: string }[]>([]);

  useEffect(() => {
    negociosService.getCategorias().then((data) => {
      const seen = new Set<string>();
      const unique = data.filter((c) => {
        if (seen.has(c.categoria)) return false;
        seen.add(c.categoria);
        return true;
      });
      setCategories(unique.map((c) => ({ name: c.categoria, icon: c.icono ?? '' })));
    }).catch(() => {
    });
  }, []);

  const [negocios, setNegocios] = useState<Restaurant[]>([]);
  const [negociosLoading, setNegociosLoading] = useState(true);

  const getFullImageUrl = (url: string | null): string | null => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    const apiBase = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:7202/api').replace('/api', '');
    return `${apiBase}${url}`;
  };

  const mapNegocio = (n: Negocio): Restaurant => {
    const imageUrl = getFullImageUrl(n.fotoNegocioUrl);
    return {
      id: n.negocioId.toString(),
      name: n.nombreNegocio,
      category: n.categoria ?? '',
      rating: 0,
      deliveryTime: '',
      deliveryFee: 0,
      image: imageUrl ?? 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=600&q=80',
      logo: imageUrl ?? '',
      banner: imageUrl ?? '',
      description: n.nombreEmpresa
    };
  };

  useEffect(() => {
    setNegociosLoading(true);
    negociosService.getNegociosPublicos()
      .then((data) => setNegocios(data.map(mapNegocio)))
      .catch(() => {})
      .finally(() => setNegociosLoading(false));
  }, []);

  const [showLocationModal, setShowLocationModal] = useState(false);
  const [savedAddress, setSavedAddress] = useState(() => localStorage.getItem('deliveryAddress') ?? '');
  const [savedPhone, setSavedPhone] = useState(() => localStorage.getItem('deliveryPhone') ?? '');
  const [modalAddress, setModalAddress] = useState('');
  const [modalPhone, setModalPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [showSmsVerification, setShowSmsVerification] = useState(false);
  const [smsCode, setSmsCode] = useState('');
  const [smsError, setSmsError] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [lastVerifiedPhone, setLastVerifiedPhone] = useState(() => localStorage.getItem('verifiedPhone') ?? '');

  const defaultCenter: [number, number] = [19.4326, -99.1332];
  const [mapCenter, setMapCenter] = useState<[number, number]>(defaultCenter);
  const [markerPos, setMarkerPos] = useState<[number, number]>(defaultCenter);
  const [geocoding, setGeocoding] = useState(false);
  const [locatingGps, setLocatingGps] = useState(false);

  const reverseGeocode = async (lat: number, lng: number): Promise<string | null> => {
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
      const data = await res.json();
      return data.display_name ?? null;
    } catch { return null; }
  };

  const flyToPosition = (pos: [number, number]) => {
    setMarkerPos(pos);
    setMapCenter(pos);
  };

  const detectCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) return;
    setLocatingGps(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const pos: [number, number] = [position.coords.latitude, position.coords.longitude];
        flyToPosition(pos);
        const addr = await reverseGeocode(pos[0], pos[1]);
        if (addr) setModalAddress(addr);
        setLocatingGps(false);
      },
      () => { setLocatingGps(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  const openLocationModal = () => {
    setModalAddress(savedAddress);
    setModalPhone(savedPhone);
    setPhoneError('');
    const storedLat = localStorage.getItem('deliveryLat');
    const storedLng = localStorage.getItem('deliveryLng');
    if (storedLat && storedLng) {
      const pos: [number, number] = [parseFloat(storedLat), parseFloat(storedLng)];
      flyToPosition(pos);
    } else if (savedAddress) {
      setMapCenter(defaultCenter);
      setMarkerPos(defaultCenter);
      setTimeout(() => {
        const q = encodeURIComponent(savedAddress.trim());
        fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${q}&limit=1`)
          .then(r => r.json())
          .then(data => {
            if (data.length > 0) flyToPosition([parseFloat(data[0].lat), parseFloat(data[0].lon)]);
          }).catch(() => {});
      }, 300);
    } else {
      setMapCenter(defaultCenter);
      setMarkerPos(defaultCenter);
    }
    setShowLocationModal(true);
  };

  const geocodeAddress = useCallback(async () => {
    if (!modalAddress.trim()) return;
    setGeocoding(true);
    try {
      const q = encodeURIComponent(modalAddress.trim());
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${q}&limit=1`);
      const data = await res.json();
      if (data.length > 0) {
        flyToPosition([parseFloat(data[0].lat), parseFloat(data[0].lon)]);
      }
    } catch { /* ignore geocoding errors */ }
    finally { setGeocoding(false); }
  }, [modalAddress]);

  const handleMapPositionSelect = async (lat: number, lng: number) => {
    setMarkerPos([lat, lng]);
    const addr = await reverseGeocode(lat, lng);
    if (addr) setModalAddress(addr);
  };

  const markerEventHandlers = useMemo(() => ({
    dragend(e: L.DragEndEvent) {
      const latlng = e.target.getLatLng();
      handleMapPositionSelect(latlng.lat, latlng.lng);
    },
  }), []);

  const handleSendVerificationCode = async () => {
    const digits = modalPhone.replace(/\D/g, '');
    if (digits.length !== 10) {
      setPhoneError('El teléfono debe ser de 10 dígitos');
      return;
    }
    if (!modalAddress.trim()) {
      return;
    }
    
    setSendingCode(true);
    setSmsError('');
    try {
      const response = await smsVerificationService.sendVerificationCode(digits);
      if (response.success) {
        setShowSmsVerification(true);
        setSmsCode(response.devCode ?? '');
      } else {
        setPhoneError(response.message);
      }
    } catch (error: any) {
      setPhoneError(error.message || 'Error al enviar el código');
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    const digits = modalPhone.replace(/\D/g, '');
    if (!smsCode.trim()) {
      setSmsError('Ingresa el código de verificación');
      return;
    }
    
    setVerifyingCode(true);
    setSmsError('');
    try {
      const response = await smsVerificationService.verifyCode(digits, smsCode.trim());
      if (response.success) {
        setPhoneVerified(true);
        setLastVerifiedPhone(digits);
        localStorage.setItem('verifiedPhone', digits);
        saveLocationData();
      } else {
        setSmsError(response.message);
      }
    } catch (error: any) {
      setSmsError(error.message || 'Código incorrecto');
    } finally {
      setVerifyingCode(false);
    }
  };

  const saveLocationData = () => {
    const digits = modalPhone.replace(/\D/g, '');
    localStorage.setItem('deliveryAddress', modalAddress.trim());
    localStorage.setItem('deliveryPhone', digits);
    localStorage.setItem('deliveryLat', markerPos[0].toString());
    localStorage.setItem('deliveryLng', markerPos[1].toString());
    setSavedAddress(modalAddress.trim());
    setSavedPhone(digits);
    setShowLocationModal(false);
    setShowSmsVerification(false);
    setSmsCode('');
  };

  const handleSaveLocation = () => {
    handleSendVerificationCode();
  };

  const formatPhoneInput = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    setModalPhone(digits);
    if (digits.length === 10 || digits.length === 0) setPhoneError('');
  };

  const [activeCategory, setActiveCategory] = useState<string>('');
  const [showAllCategories, setShowAllCategories] = useState<boolean>(true);
  const [sortByByCategory, setSortByByCategory] = useState<Record<string, SortKey>>({});

  const getSortKey = (categoryName: string): SortKey => sortByByCategory[categoryName] ?? 'recommended';

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const isSearching = normalizedQuery.length > 0;

  const filterByQuery = (items: Restaurant[]): Restaurant[] => {
    if (!isSearching) return items;
    return items.filter((item) =>
      item.name.toLowerCase().includes(normalizedQuery) ||
      item.category.toLowerCase().includes(normalizedQuery)
    );
  };

  const getItemsForCategory = (categoryName: string): Restaurant[] => {
    const raw = negocios.filter((r) => r.category === categoryName);
    const filtered = filterByQuery(raw);
    return getSortedItems(categoryName, filtered);
  };

  const parseMinDeliveryTime = (deliveryTime: string): number => {
    const match = deliveryTime.match(/\d+/);
    return match ? Number(match[0]) : Number.POSITIVE_INFINITY;
  };

  const getSortedItems = (categoryName: string, items: Restaurant[]): Restaurant[] => {
    const sortKey = getSortKey(categoryName);
    const sorted = [...items];

    if (sortKey === 'rating') {
      sorted.sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0));
      return sorted;
    }

    if (sortKey === 'deliveryTime') {
      sorted.sort((a, b) => parseMinDeliveryTime(a.deliveryTime) - parseMinDeliveryTime(b.deliveryTime));
      return sorted;
    }

    if (sortKey === 'deliveryFee') {
      sorted.sort((a, b) => (a.deliveryFee ?? 0) - (b.deliveryFee ?? 0));
      return sorted;
    }

    return sorted;
  };


  useEffect(() => {
    const ctx = gsap.context(() => {
      // Hero Animation
      gsap.fromTo(heroRef.current,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 1, ease: 'power3.out', delay: 0.2 }
      );

      // Categories staggered animation
      gsap.fromTo(".category-btn",
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.05,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: categoriesRef.current,
            start: "top 85%",
          }
        }
      );

      // Restaurants reveal
      gsap.fromTo(".restaurant-card",
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: restaurantsRef.current,
            start: "top 80%",
          }
        }
      );
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={containerRef} className="max-w-full w-full mx-auto px-1 lg:px-3 pb-32">
      {/* Hero Section */}
      <section ref={heroRef} className="py-2 lg:py-2">
        <div
          className="relative w-full rounded-[2rem] overflow-hidden min-h-[450px] lg:min-h-[520px] bg-cover bg-center flex items-center px-8 lg:px-20 shadow-premium pb-2"
          style={{ backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.7) 100%), url("https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=1200&q=80")` }}
        >
          <div className="relative z-10 max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/20 backdrop-blur-md px-4 py-4 mb-6 border border-white/20">
            </div>
            <h1 className="text-white text-5xl lg:text-7xl font-black leading-[1.1] tracking-tight mb-6">
              El sabor que amas,<br />donde tú estés.
            </h1>
            <p className="text-gray-200 text-lg lg:text-xl font-medium max-w-lg mb-10 leading-relaxed">
              Explora los mejores sabores locales con envíos flash y promociones exclusivas cada día.
            </p>
            <div
              className="flex flex-col sm:flex-row w-full max-w-[580px] md:max-w-[680px] lg:max-w-[760px] bg-white dark:bg-background-dark/90 backdrop-blur-xl rounded-[1.5rem] p-2 shadow-2xl overflow-hidden border border-white/10 cursor-pointer"
              onClick={openLocationModal}
            >
              <div className="flex flex-1 items-center px-4 py-2 min-w-0">
                <span className="material-symbols-outlined text-primary text-2xl">location_on</span>
                <span
                  className={`px-3 text-base font-medium block whitespace-normal break-words ${savedAddress ? 'text-secondary dark:text-white' : 'text-gray-400'}`}
                  title={savedAddress || ''}
                >
                  {savedAddress || '¿A dónde enviamos hoy?'}
                </span>
              </div>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openLocationModal(); }}
                className="h-14 sm:h-auto px-8 sm:px-10 rounded-2xl bg-primary text-white font-black text-base hover:bg-primary-600 transition-all shadow-lg active:scale-95 flex-shrink-0"
              >
                Explorar
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section ref={categoriesRef} className="mb-14">
        <div className="flex items-end justify-between mb-8 px-2 ">
          <div>
            <h2 className="text-2xl lg:text-3xl font-black tracking-tight text-secondary dark:text-white">Explora por Antojo</h2>
            <div className="h-1.5 w-12 bg-primary rounded-full mt-2"></div>
          </div>
          <button
            type="button"
            className="text-primary text-sm font-bold hover:underline py-1 "
            onClick={() => {
              setShowAllCategories(true);
              setActiveCategory('');
              restaurantsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          >
            Ver todo
          </button>
        </div>
        <div className=" flex gap-5 overflow-x-auto  pt-1 pb-1 px-2">
          {categories.map((cat, i) => (
            <button
              key={i}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setShowAllCategories(false);
                setActiveCategory(cat.name);
              }}
              className={`category-btn flex flex-col min-w-[120px] max-h-[120px] aspect-[4/5] shrink-0 items-center justify-center gap-4 rounded-full bg-gray-200 dark:bg-white/5 border border-gray-100 dark:border-white/5 shadow-sm hover:shadow-premium-hover  transition-all duration-300 group bg-gray-100 ${(activeCategory === cat.name && !showAllCategories) ? 'ring-2 ring-primary/60 bg-white dark:bg-white/10' : ''}`}
            >
              <div className="w-14 h-14 rounded-2xl   flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                <span className="material-symbols-outlined text-gray-700 dark:text-gray-200 text-3xl group-hover:text-primary group-hover:scale-110 transition-transform">
                  {cat.icon}
                </span>
              </div>
              <span className="text-secondary dark:text-gray-200 text-sm font-medium">{cat.name}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Offers Slider / Grid */}
      <section ref={offersRef} className="mb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-primary to-orange-700 p-10 text-white shadow-xl aspect-[21/9] md:aspect-auto flex flex-col justify-center group cursor-pointer">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl pointer-events-none group-hover:scale-110 transition-transform duration-700"></div>
            <div className="relative z-10">
              <span className="inline-block px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-[10px] font-black uppercase tracking-widest mb-4">Promoción</span>
              <h3 className="text-4xl font-black mb-2 leading-tight">50% DESC.</h3>
              <p className="text-lg font-medium opacity-90 max-w-[200px]">En tu primer pedido de McDonald's</p>
              <button className="mt-8 px-6 py-2.5 bg-white text-primary rounded-xl font-black text-sm hover:scale-105 active:scale-95 transition-all">
                Obtener Cupón
              </button>
            </div>
            <img
              className="absolute bottom-[-20px] right-[-20px] w-48 h-48 object-contain rotate-[-15deg] group-hover:rotate-0 transition-transform duration-500 opacity-20 rounded-2xl"
              src="https://lh3.googleusercontent.com/aida-public/AB6AXuAZJp-eXBU3Susd8nlKA-PuqFLD8LPif5fqKlwg15Sxe_90hAFKROxO-8fSfiC-rzTnK9hDY0e9K7577UClZZDdhyE7CfaSV-B9N7EMLP1zn3u0sD_DWaZYkuhEYZUlGj03ClE7GuvVbZmvtJMoahfxENTgbQbvhwwC5lXEXlsSVFb9A3a9K22_n2444DBjnvAVHwqgHCCi51Jz-4-p026_sX1mGtvgcpuONgfv3cR6ifPb477k9hxf1qcC5IY1tEps9Ok3MZpcRMA"
              alt="McDonald's"
              width="192"
              height="192"
              fetchPriority="high"
            />
          </div>

          <div className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-primary to-orange-700 p-10 text-white shadow-2xl aspect-[21/9] md:aspect-auto flex flex-col justify-center group cursor-pointer border border-white/10">
            <div className="absolute top-0 right-0 w-80 h-80 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-[80px] pointer-events-none group-hover:scale-125 transition-transform duration-1000"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2 blur-[60px] pointer-events-none group-hover:scale-125 transition-transform duration-1000"></div>

            <div className="relative z-10">
              <span className="inline-block px-4 py-1.5 rounded-full bg-white/20 backdrop-blur-md text-[10px] font-black uppercase tracking-[0.2em] mb-6 text-white border border-white/20">Exclusivo</span>
              <h3 className="text-4xl md:text-5xl font-black mb-3 leading-none tracking-tighter text-white">
                ENVÍO <span className="underline decoration-white decoration-4 underline-offset-8">GRATIS</span>
              </h3>
              <p className="text-lg font-bold text-white/90 max-w-[240px] leading-relaxed">En todos tus pedidos de Supermercado</p>
              <button className="mt-10 px-8 py-3 bg-white text-primary rounded-2xl font-black text-sm hover:scale-105 transition-all shadow-lg active:scale-95">
                Ver Tiendas
              </button>
            </div>
            <div className="absolute bottom-[-10px] right-[-10px] opacity-20 group-hover:opacity-30 group-hover:-translate-y-4 group-hover:-translate-x-4 transition-all duration-700">
              <span className="material-symbols-outlined text-[160px]">shopping_basket</span>
            </div>
          </div>
        </div>
      </section>

      {/* Restaurant List */}
      <section ref={restaurantsRef}>
        {negociosLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex flex-col gap-4 rounded-[2.2rem] bg-gray-200 dark:bg-white/5 p-4 animate-pulse">
                <div className="h-56 w-full rounded-[1.8rem] bg-gray-300 dark:bg-white/10" />
                <div className="px-2 pb-2 flex flex-col gap-2">
                  <div className="h-5 bg-gray-300 dark:bg-white/10 rounded-full w-3/4" />
                  <div className="h-4 bg-gray-300 dark:bg-white/10 rounded-full w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : (showAllCategories || isSearching) ? (
          <div className="flex flex-col gap-16">
            {isSearching && categories.every((cat) => getItemsForCategory(cat.name).length === 0) && (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <span className="material-symbols-outlined text-6xl text-gray-300 dark:text-gray-600 mb-6">search_off</span>
                <h3 className="text-2xl font-black text-secondary dark:text-white mb-2">Sin resultados</h3>
                <p className="text-gray-400 font-medium">No encontramos nada para "{searchQuery}"</p>
              </div>
            )}
            {categories.map((cat) => {
              const items = getItemsForCategory(cat.name);

              if (isSearching && items.length === 0) return null;

              return (
                <div key={cat.name}>
                  <div className="flex items-center justify-between mb-10 px-2">
                    <h2 className="text-2xl lg:text-3xl font-black tracking-tight text-secondary dark:text-white">{cat.name}</h2>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <span className="material-symbols-outlined text-xl text-gray-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">tune</span>
                        <select
                          value={getSortKey(cat.name)}
                          onChange={(e) => {
                            const value = e.target.value as SortKey;
                            setSortByByCategory((prev) => ({ ...prev, [cat.name]: value }));
                          }}
                          className="h-10 pl-10 pr-10 rounded-full border border-gray-200 dark:border-white/10 bg-transparent text-gray-500 hover:bg-gray-50 transition-colors text-sm font-bold appearance-none"
                        >
                          <option value="recommended">Recomendado</option>
                          <option value="rating">Mejor rating</option>
                          <option value="deliveryTime">Más rápido</option>
                          <option value="deliveryFee">Envío más barato</option>
                        </select>
                        <span className="material-symbols-outlined text-base text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">expand_more</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8   rounded-[2rem] ">
                    {items.map((res) => (
                      <div
                        key={res.id}
                        onClick={() => onRestaurantSelect(res)}
                        className="restaurant-card group flex flex-col gap-4 rounded-[2.2rem] bg-gray-200 dark:bg-white/5 p-4 shadow-sm shadow-premium-hover hover:shadow-premium-hover transition-all duration-500 border border-transparent dark:border-white/5 active:scale-[0.98] cursor-pointer"
                      >
                        <div className="relative h-56 w-full overflow-hidden rounded-[1.8rem]">
                          <img
                            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                            src={res.image}
                            alt={res.name}
                            width="600"
                            height="224"
                            loading="lazy"
                          />
                          <div className="absolute top-4 right-4 rounded-full bg-white/95 dark:bg-background-dark/90 backdrop-blur-md px-3 py-1.5 text-[11px] font-black text-secondary dark:text-white shadow-lg border border-black/5">
                            {res.deliveryTime}
                          </div>
                          {res.deliveryFee === 0 && (
                            <div className="absolute top-4 left-4 rounded-full bg-green-500 px-3 py-1.5 text-[10px] font-black text-white uppercase tracking-wider shadow-lg">
                              SinRed
                            </div>
                          )}
                        </div>
                        <div className="px-2 pb-2">
                          <div className="flex justify-between items-start mb-1">
                            <h3 className="text-xl font-black text-secondary dark:text-white line-clamp-1 group-hover:text-primary transition-colors">{res.name}</h3>
                            <div className="flex items-center gap-1 rounded-full bg-accent/10 px-2.5 py-1">
                              <span className="material-symbols-outlined text-xs text-accent filled font-bold">star</span>
                              <span className="text-xs font-black text-secondary dark:text-white">{res.rating}</span>
                            </div>
                          </div>
                          <p className="text-sm font-medium text-gray-400 mb-4">{res.category}</p>
                          <div className="flex items-center justify-between pt-4 border-t border-dashed border-gray-100 dark:border-white/10">
                            <div className="flex items-center gap-2">
                              <span className="material-symbols-outlined text-primary text-lg">delivery_dining</span>
                              <span className="text-[12px] font-bold text-gray-500 dark:text-gray-300">
                                {res.deliveryFee === 0 ? "Gratis" : `$${res.deliveryFee.toFixed(0)}`}
                              </span>
                            </div>
                            <div className="w-8 h-8 rounded-full bg-gray-50 dark:bg-white/10 flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all">
                              <span className="material-symbols-outlined text-sm">arrow_forward</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-10 px-2">
              <h2 className="text-2xl lg:text-3xl font-black tracking-tight text-secondary dark:text-white">{activeCategory}</h2>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <span className="material-symbols-outlined text-xl text-gray-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">tune</span>
                  <select
                    value={getSortKey(activeCategory)}
                    onChange={(e) => {
                      const value = e.target.value as SortKey;
                      setSortByByCategory((prev) => ({ ...prev, [activeCategory]: value }));
                    }}
                    className="h-10 pl-10 pr-10 rounded-full border border-gray-200 dark:border-white/10 bg-transparent text-gray-500 hover:bg-gray-50 transition-colors text-sm font-bold appearance-none"
                  >
                    <option value="recommended">Recomendado</option>
                    <option value="rating">Mejor rating</option>
                    <option value="deliveryTime">Más rápido</option>
                    <option value="deliveryFee">Envío más barato</option>
                  </select>
                  <span className="material-symbols-outlined text-base text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">expand_more</span>
                </div>
              </div>
            </div>

            {getItemsForCategory(activeCategory).length === 0 && (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <span className="material-symbols-outlined text-6xl text-gray-300 dark:text-gray-600 mb-6">store</span>
                <h3 className="text-2xl font-black text-secondary dark:text-white mb-2">Sin negocios</h3>
                <p className="text-gray-400 font-medium">No hay negocios en esta categoría</p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8   rounded-[2rem] ">
              {getItemsForCategory(activeCategory).map((res) => (
                <div
                  key={res.id}
                  onClick={() => onRestaurantSelect(res)}
                  className="restaurant-card group flex flex-col gap-4 rounded-[2.2rem] bg-gray-200 dark:bg-white/5 p-4 shadow-sm shadow-premium-hover hover:shadow-premium-hover transition-all duration-500 border border-transparent dark:border-white/5 active:scale-[0.98] cursor-pointer"
                >
                  <div className="relative h-56 w-full overflow-hidden rounded-[1.8rem]">
                    <img
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                      src={res.image}
                      alt={res.name}
                      width="600"
                      height="224"
                      loading="lazy"
                    />
                    <div className="absolute top-4 right-4 rounded-full bg-white/95 dark:bg-background-dark/90 backdrop-blur-md px-3 py-1.5 text-[11px] font-black text-secondary dark:text-white shadow-lg border border-black/5">
                      {res.deliveryTime}
                    </div>
                    {res.deliveryFee === 0 && (
                      <div className="absolute top-4 left-4 rounded-full bg-green-500 px-3 py-1.5 text-[10px] font-black text-white uppercase tracking-wider shadow-lg">
                        Free Delivery
                      </div>
                    )}
                  </div>
                  <div className="px-2 pb-2">
                    <div className="flex justify-between items-start mb-1">
                      <h3 className="text-xl font-black text-secondary dark:text-white line-clamp-1 group-hover:text-primary transition-colors">{res.name}</h3>
                      <div className="flex items-center gap-1 rounded-full bg-accent/10 px-2.5 py-1">
                        <span className="material-symbols-outlined text-xs text-accent filled font-bold">star</span>
                        <span className="text-xs font-black text-secondary dark:text-white">{res.rating}</span>
                      </div>
                    </div>
                    <p className="text-sm font-medium text-gray-400 mb-4">{res.category}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-dashed border-gray-100 dark:border-white/10">
                      <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-primary text-lg">delivery_dining</span>
                        <span className="text-[12px] font-bold text-gray-500 dark:text-gray-300">
                          {res.deliveryFee === 0 ? "Gratis" : `$${res.deliveryFee.toFixed(0)}`}
                        </span>
                      </div>
                      <div className="w-8 h-8 rounded-full bg-gray-50 dark:bg-white/10 flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all">
                        <span className="material-symbols-outlined text-sm">arrow_forward</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Location & Phone Modal */}
      {showLocationModal && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => setShowLocationModal(false)}
        >
          <div
            className="relative w-full max-w-lg bg-white dark:bg-slate-900 rounded-[2rem] overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative bg-gradient-to-br from-primary to-orange-600 px-8 pt-8 pb-12">
              <button
                onClick={() => setShowLocationModal(false)}
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center text-white hover:bg-white/30 transition-all"
              >
                <span className="material-symbols-outlined text-sm">close</span>
              </button>
              <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mb-4">
                <span className="material-symbols-outlined text-3xl text-white">location_on</span>
              </div>
              <h3 className="text-2xl font-black text-white">¿Dónde te entregamos?</h3>
              <p className="text-white/70 text-sm font-medium mt-1">Ingresa tu ubicación y teléfono de contacto</p>
            </div>

            {/* Form */}
            <div className="px-8 pb-8 -mt-6">
              <div className="flex flex-col gap-5 bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-lg border border-gray-100 dark:border-white/10">
                {/* Phone */}
                <div>
                  <label className="text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-2 block">
                    Teléfono (10 dígitos)
                  </label>
                  <div className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-white/10 px-4 py-3 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all">
                    <span className="material-symbols-outlined text-gray-400 text-xl">phone</span>
                    <input
                      type="tel"
                      inputMode="numeric"
                      value={modalPhone}
                      onChange={(e) => formatPhoneInput(e.target.value)}
                      placeholder="55 1234 5678"
                      maxLength={10}
                      className="flex-1 bg-transparent border-none text-secondary dark:text-white placeholder:text-gray-300 focus:outline-none text-base font-medium tracking-wide"
                    />
                    {modalPhone.length === 10 && (
                      <span className="material-symbols-outlined text-green-500 text-lg">check_circle</span>
                    )}
                  </div>
                  {phoneError && (
                    <p className="text-red-500 text-xs font-bold mt-1.5 flex items-center gap-1">
                      <span className="material-symbols-outlined text-xs">error</span>
                      {phoneError}
                    </p>
                  )}
                </div>

                {/* Address */}
                <div>
                  <label className="text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-2 block">
                    Dirección de entrega
                  </label>
                  <div className="flex items-start gap-3 rounded-xl border border-gray-200 dark:border-white/10 px-4 py-3 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all">
                    <span className="material-symbols-outlined text-gray-400 text-xl mt-0.5">home</span>
                    <textarea
                      value={modalAddress}
                      onChange={(e) => setModalAddress(e.target.value)}
                      placeholder="Calle, número, colonia, ciudad..."
                      rows={2}
                      className="flex-1 bg-transparent border-none text-secondary dark:text-white placeholder:text-gray-300 focus:outline-none text-base font-medium resize-none"
                    />
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button
                      type="button"
                      onClick={geocodeAddress}
                      disabled={!modalAddress.trim() || geocoding}
                      className="flex-1 py-2.5 rounded-xl bg-gray-100 dark:bg-white/10 text-secondary dark:text-white text-sm font-bold flex items-center justify-center gap-2 hover:bg-gray-200 dark:hover:bg-white/15 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <span className="material-symbols-outlined text-base text-primary">{geocoding ? 'progress_activity' : 'search'}</span>
                      {geocoding ? 'Buscando...' : 'Buscar en mapa'}
                    </button>
                    <button
                      type="button"
                      onClick={detectCurrentLocation}
                      disabled={locatingGps}
                      className="flex-1 py-2.5 rounded-xl bg-primary/10 dark:bg-primary/20 text-primary text-sm font-bold flex items-center justify-center gap-2 hover:bg-primary/20 dark:hover:bg-primary/30 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <span className="material-symbols-outlined text-base">{locatingGps ? 'progress_activity' : 'my_location'}</span>
                      {locatingGps ? 'Localizando...' : 'Mi ubicación'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div 
                className="mt-5 rounded-2xl overflow-hidden border border-gray-200 dark:border-white/10 shadow-sm" 
                style={{ 
                  height: 240,
                  filter: theme === 'dark' ? 'invert(90%) hue-rotate(180deg) brightness(1.1) contrast(1.1)' : 'none'
                }}
              >
                <MapContainer
                  center={mapCenter}
                  zoom={14}
                  style={{ height: '100%', width: '100%' }}
                  zoomControl={false}
                  attributionControl={false}
                  doubleClickZoom={false}
                >
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <Marker position={markerPos} draggable eventHandlers={markerEventHandlers} />
                  <MapFlyTo center={mapCenter} />
                  <MapClickHandler onDblClick={handleMapPositionSelect} />
                </MapContainer>
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-2 font-medium">
                Doble clic en el mapa o arrastra el marcador para ajustar tu ubicación
              </p>

              {/* Save Button */}
              <button
                onClick={handleSaveLocation}
                disabled={modalPhone.replace(/\D/g, '').length !== 10 || !modalAddress.trim() || sendingCode}
                className="w-full mt-6 py-4 rounded-2xl bg-primary text-white font-black text-base uppercase tracking-widest shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none flex items-center justify-center gap-2"
              >
                {sendingCode ? (
                  <>
                    <span className="material-symbols-outlined text-sm animate-spin">progress_activity</span>
                    Enviando código...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-sm">sms</span>
                    Verificar teléfono
                  </>
                )}
              </button>
            </div>
          </div>

          </div>,
        document.body
      )}

      {/* SMS Verification Modal - rendered in separate portal */}
      {showSmsVerification && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => { setShowSmsVerification(false); setSmsCode(''); setSmsError(''); }}
        >
          <div
            className="bg-white dark:bg-slate-800 rounded-3xl p-8 max-w-sm w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <span className="material-symbols-outlined text-4xl text-primary">verified</span>
              </div>
              <h3 className="text-xl font-black text-secondary dark:text-white mb-2">Verificar teléfono</h3>
              <p className="text-gray-500 text-sm">
                Enviamos un código por WhatsApp al<br />
                <span className="font-bold text-secondary dark:text-white">{modalPhone.replace(/(\d{2})(\d{4})(\d{4})/, '$1 $2 $3')}</span>
              </p>
            </div>

            <div className="mb-6">
              <label className="text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-2 block text-center">
                Código de 6 dígitos
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={smsCode}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setSmsCode(val);
                  if (val.length > 0) setSmsError('');
                }}
                placeholder="000000"
                maxLength={6}
                className="w-full text-center text-3xl font-black tracking-[0.5em] py-4 px-6 rounded-xl border-2 border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-secondary dark:text-white focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all"
                autoFocus
              />
              {smsError && (
                <p className="text-red-500 text-xs font-bold mt-2 text-center flex items-center justify-center gap-1">
                  <span className="material-symbols-outlined text-xs">error</span>
                  {smsError}
                </p>
              )}
            </div>

            <button
              onClick={handleVerifyCode}
              disabled={smsCode.length !== 6 || verifyingCode}
              className="w-full py-4 rounded-2xl bg-primary text-white font-black text-base uppercase tracking-widest shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none flex items-center justify-center gap-2"
            >
              {verifyingCode ? (
                <>
                  <span className="material-symbols-outlined text-sm animate-spin">progress_activity</span>
                  Verificando...
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined text-sm">check</span>
                  Verificar
                </>
              )}
            </button>

            <button
              onClick={() => handleSendVerificationCode()}
              disabled={sendingCode}
              className="w-full mt-3 py-3 text-primary font-bold text-sm hover:bg-primary/5 rounded-xl transition-all disabled:opacity-40"
            >
              {sendingCode ? 'Reenviando...' : '¿No recibiste el código? Reenviar'}
            </button>

            <button
              onClick={() => { setShowSmsVerification(false); setSmsCode(''); setSmsError(''); }}
              className="w-full mt-1 py-2 text-gray-400 font-medium text-sm hover:text-gray-600 transition-all"
            >
              Cambiar número
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Home;
